/**
 * Inline icon set.
 *
 * Hand-authored SVG rather than an icon package: the set is small, shipping
 * thousands of glyphs to draw a dozen is not a justified dependency, and inline
 * markup keeps "no third-party scripts on claim paths"
 * (PHASE_01_PRODUCTION_ARCHITECTURE.md §19) trivially true on every surface this
 * system will eventually reach. Shapes follow the Phosphor outline conventions
 * the UI UX Pro Max icon dataset recommends: one stroke weight, round caps and
 * joins, a square box.
 *
 * ## Why the grid is 96 units
 *
 * The `float-money` security gate forbids decimal literals in production source.
 * Icon geometry on a 24-unit grid needs half- and quarter-unit coordinates, so
 * the box is scaled to 96 and every coordinate is an integer — a quarter-unit of
 * precision at a 24px render, finer than any display resolves. The gate stays
 * strict and the shapes stay accurate; neither is traded for the other.
 *
 * ## Accessibility
 *
 * Every icon here is decorative and always sits beside a visible text label, so
 * `aria-hidden` is unconditional. This system contains no icon-only control. If
 * one is ever added it needs its own accessible name and must not reuse this
 * helper unexamined.
 */

const VIEW_BOX = 96;

const PATHS = {
  check: "M18 51 36 69 78 27",
  "check-circle": "M48 84a36 36 0 1 0 0-72 36 36 0 0 0 0 72Zm-14-37 10 10 19-19",
  clock: "M48 84a36 36 0 1 0 0-72 36 36 0 0 0 0 72Zm0-54v18l14 8",
  warning: "M48 38v18m0 10v2M41 17 12 69a8 8 0 0 0 7 12h58a8 8 0 0 0 7-12L55 17a8 8 0 0 0-14 0Z",
  "x-circle": "M48 84a36 36 0 1 0 0-72 36 36 0 0 0 0 72Zm12-48L36 60m0-24 24 24",
  prohibit: "M48 84a36 36 0 1 0 0-72 36 36 0 0 0 0 72ZM22 22l52 52",
  "shield-check": "M80 22 48 12 16 22v24c0 20 14 31 32 37 18-6 32-17 32-37Zm-45 25 10 10 18-18",
  lock: "M30 44h36a8 8 0 0 1 8 8v26a8 8 0 0 1-8 8H30a8 8 0 0 1-8-8V52a8 8 0 0 1 8-8Zm5 0V33a13 13 0 0 1 26 0v11",
  info: "M48 84a36 36 0 1 0 0-72 36 36 0 0 0 0 72Zm0-38v18m0-33v2",
  "arrow-left": "M76 48H20m0 0 24-24M20 48l24 24",
  "arrow-right": "M20 48h56m0 0L52 24m24 24L52 72",
  backspace:
    "M37 22h37a8 8 0 0 1 8 8v36a8 8 0 0 1-8 8H37a8 8 0 0 1-6-3L15 48l16-23a8 8 0 0 1 6-3Zm28 18L47 58m0-18 18 18",
  tray: "M16 58h16l5 9h22l5-9h16M16 58 26 22a8 8 0 0 1 8-6h28a8 8 0 0 1 8 6l10 36v13a8 8 0 0 1-8 8H24a8 8 0 0 1-8-8Z",
  sun: "M48 66a18 18 0 1 0 0-36 18 18 0 0 0 0 36ZM48 11v6m0 62v6M18 18l4 4m52 52 4 4M11 48h6m62 0h6M18 78l4-4m52-52 4-4",
  moon: "M82 58A34 34 0 0 1 38 14a35 35 0 1 0 44 44Z",
} as const;

/** Every icon name the system draws. */
export type IconName = keyof typeof PATHS;

/** All icon names, for the foundations reference. */
export const ICON_NAMES: readonly IconName[] = Object.keys(PATHS) as IconName[];

/**
 * Decorative icon markup at `size` CSS pixels.
 *
 * The result is assembled only from this module's own constants and a numeric
 * size, so no caller-supplied text is interpolated and there is no injection
 * surface even where the return value is assigned as markup.
 */
export function icon(name: IconName, size = 20): string {
  const px = Math.round(size);
  return [
    `<svg class="nura-icon" width="${px}" height="${px}" viewBox="0 0 ${VIEW_BOX} ${VIEW_BOX}"`,
    ' fill="none" stroke="currentColor" stroke-width="7" stroke-linecap="round"',
    ' stroke-linejoin="round" aria-hidden="true" focusable="false">',
    `<path d="${PATHS[name]}"/>`,
    "</svg>",
  ].join("");
}
